// frontend/src/pages/admin-pages/analytics.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { findSchoolOption } from "../../constants/schools";

// ─── Utilities ───────────────────────────────────────────────────────────────
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate()+n); return x; }
function toISODate(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;
}
function parseDate(x) { const d = new Date(x); return isNaN(d.getTime()) ? null : d; }
function safeNum(x, fb=0) { const n=Number(x); return isFinite(n)?n:fb; }
function fmt(n) {
  if (n===null||n===undefined) return "—";
  try { return new Intl.NumberFormat().format(n); } catch { return String(n); }
}
function schoolName(s) {
  const x = (s || "").toString().trim();
  if (!x) return "Unknown / Not provided";
  const opt = findSchoolOption(x);
  return opt ? opt.label : x;
}
function schoolValue(s) {
  const x = (s || "").toString().trim();
  if (!x) return null;
  const opt = findSchoolOption(x);
  return (opt && opt.value !== opt.label) ? opt.value : null;
}
function normalizeArray(data, key) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data[key])) return data[key];
  return [];
}
function normalizeStatus(s) {
  if (s==null) return "open";
  if (typeof s==="string") {
    const l=s.trim().toLowerCase();
    if (l==="resolved") return "resolved";
    if (l==="in_progress"||l==="in progress") return "in_progress";
    if (l==="2") return "resolved";
    if (l==="1") return "in_progress";
    return "open";
  }
  const n=Number(s);
  return n===2?"resolved":n===1?"in_progress":"open";
}
function fmtTime(dt) {
  if (!dt) return "—";
  const d=new Date(dt);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined,{month:"short",day:"2-digit",hour:"2-digit",minute:"2-digit"}).format(d);
}
function fmtShortDate(iso) {
  if (!iso) return "—";
  const d=new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined,{month:"short",day:"2-digit"}).format(d);
}
function cl(...xs) { return xs.filter(Boolean).join(" "); }

const TABS = ["Overview","Activity","Students","Schools","Reports"];

// ─── Shared UI ───────────────────────────────────────────────────────────────
function NavTab({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={cl("rounded-xl px-4 py-2 text-sm font-semibold transition",
        active?"bg-white text-slate-900":"bg-transparent text-slate-400 hover:text-white hover:bg-white/10")}>
      {label}
    </button>
  );
}

function Pill({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className={cl("rounded-full px-3 py-1.5 text-xs font-semibold transition",
        active?"bg-slate-900 text-white":"bg-slate-100 text-slate-600 hover:bg-slate-200")}>
      {label}
    </button>
  );
}

function Stat({ label, value, note }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{value}</div>
      {note && <div className="mt-1.5 text-xs text-slate-400">{note}</div>}
    </div>
  );
}

function SectionLabel({ text }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="text-xs font-bold uppercase tracking-widest text-slate-400 whitespace-nowrap">{text}</span>
      <div className="flex-1 border-t border-slate-200"/>
    </div>
  );
}

function Pagination({ page, pageSize, total, onPrev, onNext, onPageSize, hidePageSize=false }) {
  const totalPages=Math.max(1,Math.ceil(total/pageSize));
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
      <div className="text-xs text-slate-400">
        Showing{" "}
        <span className="font-semibold text-slate-700">{total===0?0:(page-1)*pageSize+1}</span>
        {" "}–{" "}
        <span className="font-semibold text-slate-700">{Math.min(page*pageSize,total)}</span>
        {" "}of <span className="font-semibold text-slate-700">{fmt(total)}</span>
      </div>
      <div className="flex items-center gap-2">
{!hidePageSize&&(
        <select value={pageSize} onChange={e=>onPageSize(Number(e.target.value))}
          className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 shadow-sm">
          {[10,25,50,100].map(n=><option key={n} value={n}>{n}/page</option>)}
        </select>
        )}
        <button type="button" onClick={onPrev} disabled={page<=1}
          className={cl("rounded-xl px-3 py-1.5 text-xs font-semibold ring-1 ring-inset",
            page<=1?"bg-slate-50 text-slate-300 ring-slate-200":"bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")}>
          ←
        </button>
        <span className="text-xs text-slate-500">{page} / {totalPages}</span>
        <button type="button" onClick={onNext} disabled={page>=totalPages}
          className={cl("rounded-xl px-3 py-1.5 text-xs font-semibold ring-1 ring-inset",
            page>=totalPages?"bg-slate-50 text-slate-300 ring-slate-200":"bg-white text-slate-700 ring-slate-200 hover:bg-slate-50")}>
          →
        </button>
      </div>
    </div>
  );
}

function DataTable({ cols, rows, empty="No data." }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="max-h-[560px] overflow-auto">
        <table className="w-full text-left text-sm">
          <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-400">
            <tr>
              {cols.map(c=>(
                <th key={c.key} className="px-4 py-3 whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length===0?(
              <tr><td className="px-4 py-8 text-slate-400 text-sm" colSpan={cols.length}>{empty}</td></tr>
            ):rows.map((r,i)=>(
              <tr key={r._key||r.id||i} className="hover:bg-slate-50/60">
                {cols.map(c=>(
                  <td key={c.key} className="px-4 py-3 text-slate-700">
                    {typeof c.render==="function"?c.render(r,i):r[c.key]??"—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Single chart component ───────────────────────────────────────────────────
function Chart({ series=[], color="#3b82f6", label="", total }) {
  const W=1200, H=210, PL=52, PR=52, PT=16, PB=44;
  const vals=series.map(s=>safeNum(s.value,0));
  const maxV=Math.max(1,...vals);
  const n=series.length;
  const xAt=i=>PL+(n<2?(W-PL-PR)/2:i*(W-PL-PR)/(n-1));
  const yAt=v=>PT+(1-(v/maxV))*(H-PT-PB);
  const pts=series.map((s,i)=>({x:xAt(i),y:yAt(safeNum(s.value,0)),v:safeNum(s.value,0),date:s.date}));
  const dLine=pts.map((p,i)=>`${i===0?"M":"L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const dArea=pts.length?`${dLine} L ${pts[pts.length-1].x} ${H-PB} L ${pts[0].x} ${H-PB} Z`:"";

  // Build x-axis ticks enforcing a minimum pixel gap so labels never merge.
  // At W=1200, each "Feb 26" label is ~42 SVG units wide; use 64 as safe gap.
  const MIN_GAP = 64;
  const xTicks = useMemo(() => {
    if (n === 0) return [];
    if (n === 1) return [pts[0]];
    const result = [0];
    let lastX = pts[0].x;
    for (let i = 1; i < n - 1; i++) {
      if (pts[i].x - lastX >= MIN_GAP) {
        result.push(i);
        lastX = pts[i].x;
      }
    }
    // Last tick: add only if it has enough clearance, otherwise replace the
    // penultimate selected tick so the rightmost label is always the end date.
    const lastPtX = pts[n - 1].x;
    if (lastPtX - lastX >= MIN_GAP) {
      result.push(n - 1);
    } else {
      result[result.length - 1] = n - 1;
    }
    return result.map(i => pts[i]);
  }, [pts, n]); // eslint-disable-line

  const gradId=`grad-${label.replace(/\W/g,"_")}`;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{background:color}}/>
          <span className="text-sm font-bold text-slate-900">{label}</span>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-400 uppercase tracking-wide">Total</div>
          <div className="text-lg font-extrabold text-slate-900 tracking-tight">{fmt(total)}</div>
        </div>
      </div>
      <div className="rounded-xl bg-slate-50 ring-1 ring-slate-100 overflow-hidden">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{height:"170px"}}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.2"/>
              <stop offset="100%" stopColor={color} stopOpacity="0"/>
            </linearGradient>
          </defs>
          {[0,1,2,3,4].map(k=>{
            const y=PT+(k*(H-PT-PB)/4);
            const v=Math.round(maxV*(1-k/4));
            return (
              <g key={k}>
                <line x1={PL} x2={W-PR} y1={y} y2={y} stroke="rgba(0,0,0,0.06)" strokeWidth="1"/>
                <text x={PL-8} y={y+4} fontSize="11" fill="#94a3b8" textAnchor="end">{v}</text>
              </g>
            );
          })}
          <line x1={PL} x2={W-PR} y1={H-PB} y2={H-PB} stroke="rgba(0,0,0,0.12)" strokeWidth="1"/>
          {xTicks.map((p,i)=>(
            <g key={i}>
              <line x1={p.x} x2={p.x} y1={H-PB} y2={H-PB+5} stroke="rgba(0,0,0,0.12)"/>
              <text x={p.x} y={H-PB+20} fontSize="11" fill="#94a3b8" textAnchor="middle">{fmtShortDate(p.date)}</text>
            </g>
          ))}
          {n===0?(
            <text x="50%" y="50%" textAnchor="middle" fill="#cbd5e1" fontSize="14">No data</text>
          ):(
            <>
              <path d={dArea} fill={`url(#${gradId})`}/>
              <path d={dLine} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"/>
              {pts.map((p,i)=>(
                <g key={i}>
                  <circle cx={p.x} cy={p.y} r="4" fill={color} stroke="#fff" strokeWidth="2"/>
                  <title>{`${p.date}: ${p.v}`}</title>
                </g>
              ))}
            </>
          )}
        </svg>
      </div>
      <div className="flex justify-between mt-2 text-xs text-slate-400 px-1">
        <span>{series[0]?.date||"—"}</span>
        <span>{series[series.length-1]?.date||"—"}</span>
      </div>
    </div>
  );
}

// ─── Date Range Picker ────────────────────────────────────────────────────────
function DateRangePicker({ rangePreset, setRangePreset, startDate, endDate, onApply }) {
  const [draftStart, setDraftStart] = useState(startDate);
  const [draftEnd,   setDraftEnd]   = useState(endDate);

  useEffect(()=>{ setDraftStart(startDate); setDraftEnd(endDate); },[startDate,endDate]);

  const presets=[{key:"7d",label:"7 days"},{key:"30d",label:"30 days"},{key:"90d",label:"90 days"},{key:"all",label:"All time"}];

  const handleApply=()=>{
    let s=draftStart, e=draftEnd;
    if (!s||!e) return;
    if (new Date(s)>new Date(e)) { const t=s; s=e; e=t; }
    onApply(s,e);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      {/* Preset row */}
      <div className="flex flex-wrap items-center gap-2 px-5 py-3 bg-slate-50 border-b border-slate-100">
        {presets.map(p=>(
          <button key={p.key} type="button" onClick={()=>setRangePreset(p.key)}
            className={cl(
              "rounded-lg px-4 py-1.5 text-xs font-semibold transition border",
              rangePreset===p.key
                ?"bg-slate-900 text-white border-slate-900"
                :"bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
            )}>
            {p.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-400 font-mono">{startDate} → {endDate}</span>
      </div>
      {/* Custom row */}
      <div className="flex flex-wrap items-end gap-3 px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-slate-500">From</label>
          <input type="date" value={draftStart}
            onChange={e=>{setDraftStart(e.target.value); setRangePreset("custom");}}
            style={{minWidth:"160px"}}
            className="w-44 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition"/>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold text-slate-500">To</label>
          <input type="date" value={draftEnd}
            onChange={e=>{setDraftEnd(e.target.value); setRangePreset("custom");}}
            style={{minWidth:"160px"}}
            className="w-44 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition"/>
        </div>
        <button type="button" onClick={handleApply}
          className="rounded-xl bg-slate-900 px-6 py-2 text-sm font-semibold text-white hover:bg-slate-700 transition shadow-sm">
          Apply
        </button>
        {rangePreset==="custom"&&(
          <span className="self-center text-xs text-slate-400 italic">Custom range active</span>
        )}
      </div>
    </div>
  );
}

// ─── Student Drawer ───────────────────────────────────────────────────────────
function StudentDrawer({ student, onClose }) {
  const [detail,  setDetail]  = useState(null);
  const [loading, setLoading] = useState(false);
  const [err,     setErr]     = useState("");

  useEffect(()=>{
    if (!student) return;
    setDetail(null); setErr(""); setLoading(true);
    axios.get(`${API_BASE_URL}/api/admin/analytics/student_detail/${student.id}`)
      .then(r=>{ setDetail(r.data); })
      .catch(e=>{
        const msg = e?.response?.data?.error || e?.response?.data?.message || e?.message || "Failed to load student detail.";
        setErr(msg);
      })
      .finally(()=>setLoading(false));
  },[student?.id]); // eslint-disable-line

  if (!student) return null;

  // Normalise detail fields — support various response shapes
  const d = detail || {};
  const profile = d.student || d.profile || d || {};
  const questionsAnswered = safeNum(
    d.questions_answered ?? d.total_questions_answered ?? d.questions_touched ??
    profile.questions_answered ?? profile.total_questions_answered ?? null,
    null
  );
  const questionsCorrect = safeNum(
    d.questions_correct ?? d.correct_answers ?? profile.questions_correct ?? null,
    null
  );
  const lastLoginAt   = d.last_login_at   ?? profile.last_login_at   ?? null;
  const lastPracticed = d.last_practiced_at ?? profile.last_practiced_at ?? null;
  const enrollments = d.enrollments ?? profile.enrollments ?? null;
  const enrollCount = Array.isArray(enrollments) ? enrollments.length : safeNum(d.enrollment_count ?? profile.enrollment_count, null);

  const initials = [student.name?.split(" ")[0]?.[0], student.name?.split(" ")[1]?.[0]].filter(Boolean).join("").toUpperCase() || "?";

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.45)",zIndex:40,backdropFilter:"blur(2px)"}}
      />
      {/* Drawer */}
      <div style={{
        position:"fixed",top:0,right:0,bottom:0,width:"min(480px,100vw)",
        background:"#fff",zIndex:50,display:"flex",flexDirection:"column",
        boxShadow:"-4px 0 32px rgba(0,0,0,0.12)",
        animation:"slideIn 0.22s ease",
      }}>
        <style>{`@keyframes slideIn{from{transform:translateX(100%)}to{transform:translateX(0)}}`}</style>

        {/* Header */}
        <div style={{background:"#0f172a",padding:"24px 24px 20px",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:"12px"}}>
            <div style={{display:"flex",alignItems:"center",gap:"14px"}}>
              <div style={{
                width:48,height:48,borderRadius:"50%",background:"#334155",
                display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:"18px",fontWeight:800,color:"#f1f5f9",flexShrink:0,
              }}>{initials}</div>
              <div>
                <div style={{fontSize:"17px",fontWeight:800,color:"#f8fafc",letterSpacing:"-0.02em"}}>{student.name}</div>
                <div style={{fontSize:"12px",color:"#94a3b8",marginTop:"2px"}}>{student.email}</div>
              </div>
            </div>
            <button onClick={onClose} style={{
              background:"rgba(255,255,255,0.08)",border:"none",borderRadius:"10px",
              width:36,height:36,display:"flex",alignItems:"center",justifyContent:"center",
              color:"#94a3b8",cursor:"pointer",fontSize:"18px",flexShrink:0,
            }}>×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:"auto",padding:"20px 24px"}}>
          {loading&&(
            <div style={{textAlign:"center",padding:"40px 0",color:"#94a3b8",fontSize:"14px"}}>Loading…</div>
          )}
          {err&&!loading&&(
            <div style={{background:"#fef2f2",border:"1px solid #fecaca",borderRadius:"12px",padding:"12px 14px",
              fontSize:"13px",color:"#b91c1c",marginBottom:"16px"}}>{err}</div>
          )}

          {/* Profile fields — always shown from student row data */}
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px",marginBottom:"20px"}}>
            {[
              {label:"Student ID",    value: student.id},
              {label:"School",        value: schoolName(student.school), subValue: schoolValue(student.school)},
              {label:"Joined",        value: fmtTime(student.created_at)},
              {label:"Last login",    value: lastLoginAt   ? fmtTime(lastLoginAt)   : "—"},
              {label:"Last practiced",value: lastPracticed ? fmtTime(lastPracticed) : "—"},
              {label:"Enrollments",   value: enrollCount!=null ? fmt(enrollCount) : "—"},
            ].map(item=>(
              <div key={item.label} style={{
                background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:"12px",
                padding:"12px 14px",
              }}>
                <div style={{fontSize:"10px",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",color:"#94a3b8"}}>{item.label}</div>
                <div style={{fontSize:"14px",fontWeight:600,color:"#0f172a",marginTop:"4px",wordBreak:"break-all"}}>{item.value||"—"}</div>
                {item.subValue&&<div style={{fontSize:"10px",fontStyle:"italic",color:"#94a3b8",marginTop:"2px"}}>{item.subValue}</div>}
              </div>
            ))}
          </div>

          {/* Questions stats */}
          <div style={{marginBottom:"20px"}}>
            <div style={{fontSize:"11px",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",
              color:"#94a3b8",marginBottom:"10px"}}>Question activity</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px"}}>
              <div style={{
                background: questionsAnswered!=null&&questionsAnswered>0 ? "#f0fdf4":"#f8fafc",
                border:`1px solid ${questionsAnswered!=null&&questionsAnswered>0?"#bbf7d0":"#e2e8f0"}`,
                borderRadius:"14px",padding:"16px",
              }}>
                <div style={{fontSize:"10px",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",color:"#64748b"}}>
                  Questions answered
                </div>
                <div style={{fontSize:"30px",fontWeight:800,color:"#0f172a",letterSpacing:"-0.03em",marginTop:"6px"}}>
                  {questionsAnswered!=null ? fmt(questionsAnswered) : "—"}
                </div>
                {questionsAnswered==null&&!loading&&(
                  <div style={{fontSize:"11px",color:"#94a3b8",marginTop:"4px"}}>Not available from API</div>
                )}
              </div>
              <div style={{
                background: questionsCorrect!=null&&questionsCorrect>0 ? "#eff6ff":"#f8fafc",
                border:`1px solid ${questionsCorrect!=null&&questionsCorrect>0?"#bfdbfe":"#e2e8f0"}`,
                borderRadius:"14px",padding:"16px",
              }}>
                <div style={{fontSize:"10px",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",color:"#64748b"}}>
                  Correct answers
                </div>
                <div style={{fontSize:"30px",fontWeight:800,color:"#0f172a",letterSpacing:"-0.03em",marginTop:"6px"}}>
                  {questionsCorrect!=null ? fmt(questionsCorrect) : "—"}
                </div>
                {questionsCorrect!=null&&questionsAnswered!=null&&questionsAnswered>0&&(
                  <div style={{fontSize:"12px",color:"#3b82f6",marginTop:"4px",fontWeight:600}}>
                    {((questionsCorrect/questionsAnswered)*100).toFixed(1)}% accuracy
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Enrollments list */}
          {Array.isArray(enrollments)&&enrollments.length>0&&(
            <div>
              <div style={{fontSize:"11px",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.08em",
                color:"#94a3b8",marginBottom:"10px"}}>Enrolled courses</div>
              <div style={{display:"flex",flexDirection:"column",gap:"8px"}}>
                {enrollments.map((e,i)=>(
                  <div key={i} style={{
                    background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:"12px",
                    padding:"10px 14px",fontSize:"13px",color:"#334155",fontWeight:500,
                  }}>
                    {e.course_name||e.name||e.subject||e.title||`Course #${e.id||i+1}`}
                    {e.enrolled_at&&<span style={{color:"#94a3b8",fontWeight:400,marginLeft:"8px",fontSize:"12px"}}>{fmtTime(e.enrolled_at)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw fallback — show whatever the API returned nicely */}
          {!loading&&detail&&!err&&questionsAnswered==null&&(
            <details style={{marginTop:"16px"}}>
              <summary style={{fontSize:"12px",color:"#94a3b8",cursor:"pointer",userSelect:"none"}}>
                Raw API response (tap to inspect)
              </summary>
              <pre style={{fontSize:"11px",color:"#475569",background:"#f8fafc",border:"1px solid #e2e8f0",
                borderRadius:"10px",padding:"12px",overflowX:"auto",marginTop:"8px",whiteSpace:"pre-wrap"}}>
                {JSON.stringify(detail,null,2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Analytics() {
  const today    = useMemo(()=>startOfDay(new Date()),[]);
  const todayKey = useMemo(()=>toISODate(today),[today]);

  const [tab,           setTab]           = useState("Overview");
  const [rangePreset,   setRangePreset]   = useState("7d");
  const [startDate,     setStartDate]     = useState(toISODate(addDays(today,-6)));
  const [endDate,       setEndDate]       = useState(toISODate(today));

  const [loading,       setLoading]       = useState(false);
  const [err,           setErr]           = useState("");

  const [students,      setStudents]      = useState([]);
  const [reports,       setReports]       = useState([]);
  const [schoolsData,   setSchoolsData]   = useState(null);
  const [todaySummary,  setTodaySummary]  = useState(null);
  const [windowSummary, setWindowSummary] = useState(null);

  // Student table state
  const [studentSearch,   setStudentSearch]   = useState("");
  const [studentSort,     setStudentSort]     = useState("newest");
  const [studentPage,     setStudentPage]     = useState(1);
  const [studentPageSize, setStudentPageSize] = useState(25);

  // School table state
  const [schoolsMode,    setSchoolsMode]    = useState("all_time");
  const [schoolSearch,   setSchoolSearch]   = useState("");
  const [schoolPage,     setSchoolPage]     = useState(1);
  const [schoolPageSize, setSchoolPageSize] = useState(25);

  // Report table state
  const [reportStatus,    setReportStatus]    = useState("open");
  const [reportSearch,    setReportSearch]    = useState("");
  const [reportPage,      setReportPage]      = useState(1);
  const [reportPageSize,  setReportPageSize]  = useState(25);

  // Student drawer
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Questions answered map: student_id -> count
  const [questionsMap, setQuestionsMap] = useState({});
  const [uqsData,      setUqsData]      = useState(null);

  // Activity tab
  const [activityData,  setActivityData]  = useState(null);
  const [sessionType,   setSessionType]   = useState("Student");
  const [activityLoading, setActivityLoading] = useState(false);
  const [sessionPage,     setSessionPage]     = useState(1);
  const SESSION_PAGE_SIZE = 10;

  // ── Preset effect ──
  useEffect(()=>{
    if (rangePreset==="7d")        apply(addDays(today,-6),today);
    else if (rangePreset==="30d")  apply(addDays(today,-29),today);
    else if (rangePreset==="90d")  apply(addDays(today,-89),today);
    else if (rangePreset==="all") {
      const e=students.map(s=>new Date(s.created_at)).filter(d=>!isNaN(d.getTime())).sort((a,b)=>a-b)[0];
      apply(e||addDays(today,-89),today);
    }
  },[rangePreset,today]); // eslint-disable-line

  function apply(s,e){ setStartDate(toISODate(s)); setEndDate(toISODate(e)); }

  const windowRange=useMemo(()=>({
    start:startOfDay(new Date(startDate)),
    end:addDays(startOfDay(new Date(endDate)),1),
  }),[startDate,endDate]);

  // ── Fetching ──
  const fetchBase=async()=>{
    const [sRes,rRes,schRes]=await Promise.all([
      axios.get(`${API_BASE_URL}/students`),
      axios.get(`${API_BASE_URL}/question_reports`),
      axios.get(`${API_BASE_URL}/api/admin/analytics/schools_all_time`),
    ]);
    setStudents(normalizeArray(sRes.data,"students"));
    setReports(normalizeArray(rRes.data,"reports"));
    setSchoolsData(schRes.data);
  };
  const fetchToday=async()=>{
    const r=await axios.get(`${API_BASE_URL}/api/admin/analytics/summary`,{params:{start_date:todayKey,end_date:todayKey}});
    setTodaySummary(r.data);
  };
  const fetchWindow=async()=>{
    const r=await axios.get(`${API_BASE_URL}/api/admin/analytics/summary`,{params:{start_date:startDate,end_date:endDate}});
    setWindowSummary(r.data);
  };
  const fetchQuestionsMap=async()=>{
    const r=await axios.get(`${API_BASE_URL}/api/admin/analytics/uqs_activity`,{
      params:{start_date:"2020-01-01",end_date:toISODate(new Date())}
    });
    const top=r.data?.top_students||[];
    const map={};
    top.forEach(s=>{ map[s.student_id]=s.questions_touched; });
    setQuestionsMap(map);
  };

  const fetchUqsWindow=async()=>{
    const r=await axios.get(`${API_BASE_URL}/api/admin/analytics/uqs_activity`,{
      params:{start_date:startDate,end_date:endDate}
    });
    setUqsData(r.data);
  };

  const fetchActivity=async(type=sessionType)=>{
    setActivityLoading(true);
    try {
      const r=await axios.get(`${API_BASE_URL}/api/admin/analytics/auth_sessions`,{
        params:{start_date:startDate,end_date:endDate,type}
      });
      setActivityData(r.data);
    } catch(e){ setErr(e?.response?.data?.error||e?.message||"Failed to load activity."); }
    finally { setActivityLoading(false); }
  };

  const refreshAll=async()=>{
    setLoading(true); setErr("");
    try { await Promise.all([fetchBase(),fetchToday(),fetchWindow(),fetchQuestionsMap(),fetchUqsWindow(),fetchActivity("Student")]); }
    catch(e){ setErr(e?.response?.data?.error||e?.message||"Failed to load."); }
    finally { setLoading(false); }
  };

  useEffect(()=>{ refreshAll(); },[]);                                             // eslint-disable-line
  useEffect(()=>{ fetchWindow().catch(e=>setErr(e?.message||"")); },[startDate,endDate]); // eslint-disable-line
  useEffect(()=>{ fetchUqsWindow().catch(e=>setErr(e?.message||"")); },[startDate,endDate]); // eslint-disable-line
  useEffect(()=>{ fetchActivity(sessionType).catch(e=>setErr(e?.message||"")); },[startDate,endDate,sessionType]); // eslint-disable-line
  useEffect(()=>{ fetchToday().catch(e=>setErr(e?.message||"")); },[todayKey]);           // eslint-disable-line

  // ── TODAY ──
  const todayRow=useMemo(()=>{
    const d=todaySummary?.daily||[];
    return d.find(x=>x.date===todayKey)||d[0]||null;
  },[todaySummary,todayKey]);

  const tSignups   = safeNum(todayRow?.signups,0);
  const tLogins    = safeNum(todayRow?.unique_student_logins,0);
  const tPracticed = safeNum(todayRow?.practice_dau,0);
  const tQTouched  = safeNum(todayRow?.questions_touched,0);
  const tReports   = useMemo(()=>reports.filter(r=>{
    const d=parseDate(r.created_at); return d&&d>=today&&d<addDays(today,1);
  }).length,[reports,today]);

  // ── WINDOW ──
  const daily=useMemo(()=>windowSummary?.daily||[],[windowSummary]);
  const signupsSeries  = useMemo(()=>daily.map(d=>({date:d.date,value:safeNum(d.signups,0)})),[daily]);
  const loginsSeries   = useMemo(()=>daily.map(d=>({date:d.date,value:safeNum(d.unique_student_logins,0)})),[daily]);
  const practiceSeries = useMemo(()=>daily.map(d=>({date:d.date,value:safeNum(d.questions_touched,0)})),[daily]);

  const wSignups   = useMemo(()=>signupsSeries.reduce((a,x)=>a+x.value,0),[signupsSeries]);
  const wLogins    = useMemo(()=>loginsSeries.reduce((a,x)=>a+x.value,0),[loginsSeries]);
  const wQTouched  = useMemo(()=>practiceSeries.reduce((a,x)=>a+x.value,0),[practiceSeries]);

  const studentsInWindow=useMemo(()=>students.filter(s=>{
    const d=parseDate(s.created_at); return d&&d>=windowRange.start&&d<windowRange.end;
  }),[students,windowRange]);

  const reportsInWindow=useMemo(()=>reports.filter(r=>{
    const d=parseDate(r.created_at); return d&&d>=windowRange.start&&d<windowRange.end;
  }),[reports,windowRange]);

  // ── Students ──
  const studentRows=useMemo(()=>{
    const q=studentSearch.trim().toLowerCase();
    let base=studentsInWindow.map(s=>({
      id:s.id,
      name:`${s.first_name||""} ${s.last_name||""}`.trim()||"—",
      email:s.email||"—",
      school:schoolName(s.school),
      schoolRaw:schoolValue(s.school),
      created_at:s.created_at,
      questionsAnswered:questionsMap[s.id]??null,
    }));
    if(q) base=base.filter(r=>`${r.id} ${r.name} ${r.email} ${r.school}`.toLowerCase().includes(q));
    if(studentSort==="newest")    base.sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
    else if(studentSort==="oldest")   base.sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));
    else if(studentSort==="name_az")  base.sort((a,b)=>a.name.localeCompare(b.name));
    else if(studentSort==="school_az")base.sort((a,b)=>a.school.localeCompare(b.school));
    else if(studentSort==="most_questions")base.sort((a,b)=>(b.questionsAnswered??-1)-(a.questionsAnswered??-1));
    else if(studentSort==="least_questions")base.sort((a,b)=>(a.questionsAnswered??Infinity)-(b.questionsAnswered??Infinity));
    return base;
  },[studentsInWindow,studentSearch,studentSort,questionsMap]);

  useEffect(()=>setStudentPage(1),[studentSearch,studentSort,studentPageSize,startDate,endDate]);
  const studentTotal   =studentRows.length;
  const studentTotalPgs=Math.max(1,Math.ceil(studentTotal/studentPageSize));
  const studentPageRows=useMemo(()=>{
    const s=(studentPage-1)*studentPageSize;
    return studentRows.slice(s,s+studentPageSize).map((r,i)=>({...r,_rank:(studentPage-1)*studentPageSize+i+1}));
  },[studentRows,studentPage,studentPageSize]);

  // ── Schools ──
  const schoolsWindowAll=useMemo(()=>{
    const m=new Map();
    for(const s of studentsInWindow){const n=schoolName(s.school);m.set(n,(m.get(n)||0)+1);}
    return Array.from(m.entries()).map(([school,students])=>({school,students,schoolRaw:null})).sort((a,b)=>b.students-a.students);
  },[studentsInWindow]);
  const schoolsAllTimeAll=useMemo(()=>[...(schoolsData?.by_school||[])].map(r=>({...r,school:schoolName(r.school),schoolRaw:schoolValue(r.school)})).sort((a,b)=>(b.students||0)-(a.students||0)),[schoolsData]);

  const schoolRows=useMemo(()=>{
    const q=schoolSearch.trim().toLowerCase();
    const base=(schoolsMode==="window"?schoolsWindowAll:schoolsAllTimeAll).map((r,i)=>({
      _key:`sch-${i}`,school:r.school,schoolRaw:r.schoolRaw,students:r.students,pct_of_total:r.pct_of_total,
    }));
    return q?base.filter(r=>`${r.school}`.toLowerCase().includes(q)):base;
  },[schoolsMode,schoolsWindowAll,schoolsAllTimeAll,schoolSearch]);

  useEffect(()=>setSchoolPage(1),[schoolSearch,schoolPageSize,schoolsMode]);
  const schoolTotal   =schoolRows.length;
  const schoolTotalPgs=Math.max(1,Math.ceil(schoolTotal/schoolPageSize));
  const schoolPageRows=useMemo(()=>{
    const s=(schoolPage-1)*schoolPageSize;
    return schoolRows.slice(s,s+schoolPageSize).map((r,i)=>({...r,_rank:(schoolPage-1)*schoolPageSize+i+1}));
  },[schoolRows,schoolPage,schoolPageSize]);

  // ── Reports ──
  const reportRows=useMemo(()=>{
    const q=reportSearch.trim().toLowerCase();
    let base=reportsInWindow.map(r=>({
      id:r.id||`${r.question_id}-${r.created_at}`,
      created_at:r.created_at,
      question_id:r.question_id??"—",
      issue_type:r.issue_type??"—",
      status:normalizeStatus(r.status),
      student_id:r.student_id??"—",
      description:r.description??"",
    })).filter(r=>reportStatus==="all"||r.status===reportStatus)
      .sort((a,b)=>new Date(b.created_at)-new Date(a.created_at));
    return q?base.filter(r=>`${r.id} ${r.question_id} ${r.issue_type} ${r.status} ${r.description}`.toLowerCase().includes(q)):base;
  },[reportsInWindow,reportStatus,reportSearch]);

  useEffect(()=>setReportPage(1),[reportStatus,reportSearch,reportPageSize,startDate,endDate]);
  const reportTotal   =reportRows.length;
  const reportTotalPgs=Math.max(1,Math.ceil(reportTotal/reportPageSize));
  const reportPageRows=useMemo(()=>{
    const s=(reportPage-1)*reportPageSize;
    return reportRows.slice(s,s+reportPageSize);
  },[reportRows,reportPage,reportPageSize]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Header ── */}
      <header className="bg-slate-900">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between gap-4 pt-7 pb-2 flex-wrap">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-white">Analytics</h1>
              <p className="mt-0.5 text-sm text-slate-400">EduBahamas</p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <span className="flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1 text-xs text-slate-400">
                <span className={cl("inline-block w-1.5 h-1.5 rounded-full",loading?"bg-amber-400 animate-pulse":"bg-emerald-400")}/>
                {loading?"Refreshing…":"Live"}
              </span>
              <button type="button" onClick={refreshAll}
                className="rounded-xl bg-white px-4 py-1.5 text-sm font-semibold text-slate-900 hover:bg-slate-100 transition">
                ↻ Refresh
              </button>
            </div>
          </div>
          <nav className="flex flex-wrap gap-1 border-t border-white/10 pt-3 pb-4 mt-4">
            {TABS.map(t=><NavTab key={t} label={t} active={tab===t} onClick={()=>setTab(t)}/>)}
          </nav>
        </div>
      </header>

      {/* ── Body ── */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 space-y-5">
        {err&&(
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">⚠ {err}</div>
        )}

        {/* Date range picker — always visible */}
        <DateRangePicker
          rangePreset={rangePreset}
          setRangePreset={setRangePreset}
          startDate={startDate}
          endDate={endDate}
          onApply={(s,e)=>{ setRangePreset("custom"); setStartDate(s); setEndDate(e); }}
        />

        {/* ══ OVERVIEW ══════════════════════════════════════════════════════ */}
        {tab==="Overview"&&(()=>{
          // ── derived overview stats ──
          const correct   = safeNum(uqsData?.totals?.correct_last_results,0);
          const incorrect = safeNum(uqsData?.totals?.incorrect_last_results,0);
          const partial   = safeNum(uqsData?.totals?.partial_last_results,0);
          const totalAttempts = correct + incorrect + partial;
          // Accuracy comes from the server in marks, the same figure students see.
          // The count-based fallback is only for a server that predates that field.
          const serverAccuracy = uqsData?.totals?.accuracy_percentage;
          const accuracyPct =
            serverAccuracy != null
              ? Number(serverAccuracy).toFixed(1)
              : totalAttempts>0 ? ((correct/totalAttempts)*100).toFixed(1) : null;

          const neverPracticed = uqsData?.never_practiced_new_students||[];
          const neverPracticedCount = neverPracticed.length;
          const newStudentsInWindow = studentsInWindow.length;
          const neverPracticedPct = newStudentsInWindow>0
            ? ((neverPracticedCount/newStudentsInWindow)*100).toFixed(0)
            : null;

          // avg logins per active student in window (login-days / unique students who logged in)
          const totalLoginDays = safeNum(windowSummary?.totals?.unique_student_logins,0);
          const uniqueLoginStudents = (windowSummary?.daily||[])
            .flatMap(()=>0) // placeholder — we sum login-days and divide by days active
            .length;
          // simpler: total login-days / students who signed up (rough engagement rate)
          const avgLoginsPerStudent = newStudentsInWindow>0
            ? (totalLoginDays/newStudentsInWindow).toFixed(1)
            : null;

          const topSchool = (schoolsData?.by_school||[])[0];

          return (
          <>
            <SectionLabel text={`Today · ${todayKey}`}/>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5">
              <Stat label="Signups"            value={fmt(tSignups)}   note="New accounts"/>
              <Stat label="Logins"             value={fmt(tLogins)}    note="Unique students"/>
              <Stat label="Practiced"          value={fmt(tPracticed)} note="Studied today"/>
              <Stat label="Questions answered" value={fmt(tQTouched)}  note="Total touches"/>
              <Stat label="Reports filed"      value={fmt(tReports)}   note="Issues today"/>
            </div>

            <div className="pt-2"/>
            <SectionLabel text={`Window · ${startDate} → ${endDate}`}/>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Stat label="New students"       value={fmt(newStudentsInWindow)}     note="Accounts created"/>
              <Stat label="Total logins"       value={fmt(wLogins)}                 note="Login-days in window"/>
              <Stat label="Avg logins / student" value={avgLoginsPerStudent??'—'}   note="Engagement rate"/>
              <Stat label="Questions answered" value={fmt(wQTouched)}               note="Total in window"/>
            </div>

            <div className="pt-2"/>
            <SectionLabel text="Engagement quality"/>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              <Stat
                label="Accuracy rate"
                value={accuracyPct!=null?`${accuracyPct}%`:"—"}
                note={totalAttempts>0?`${fmt(correct)} correct of ${fmt(totalAttempts)}`:"No attempts yet"}
              />
              <Stat
                label="Never practiced"
                value={fmt(neverPracticedCount)}
                note={neverPracticedPct!=null?`${neverPracticedPct}% of new signups signed up but never answered`:"of new signups"}
              />
              <Stat
                label="Top school"
                value={topSchool?schoolName(topSchool.school):"—"}
                note={topSchool?`${fmt(topSchool.students)} students all time`:""}
              />
              <Stat
                label="Practice rate"
                value={wQTouched>0&&wLogins>0?`${(wQTouched/wLogins).toFixed(1)}`:"—"}
                note="Avg questions per login"
              />
            </div>

            {/* Never practiced list — collapsible */}
            {neverPracticedCount>0&&(
              <details className="rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden">
                <summary className="px-5 py-3 text-xs font-bold uppercase tracking-wider text-amber-700 cursor-pointer select-none flex items-center gap-2">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
                    <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
                  </svg>
                  {neverPracticedCount} new student{neverPracticedCount!==1?"s":""} signed up but never practiced — tap to review
                </summary>
                <div className="px-5 pb-4 pt-1">
                  <div className="overflow-hidden rounded-xl border border-amber-200 bg-white">
                    <table className="w-full text-left text-sm">
                      <thead className="bg-amber-50 text-xs font-bold uppercase tracking-wider text-amber-600">
                        <tr>
                          {["Name","Email","School","Joined"].map(h=>(
                            <th key={h} className="px-4 py-2 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {neverPracticed.map((s,i)=>(
                          <tr key={s.id||i} className="hover:bg-amber-50/60">
                            <td className="px-4 py-2 font-semibold text-slate-800">{`${s.first_name||""} ${s.last_name||""}`.trim()||"—"}</td>
                            <td className="px-4 py-2 text-slate-500 text-xs">{s.email||"—"}</td>
                            <td className="px-4 py-2 text-slate-500 text-xs">{schoolName(s.school)}</td>
                            <td className="px-4 py-2 text-slate-400 text-xs">{fmtTime(s.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </details>
            )}
          </>
          );
        })()}


        {/* ══ ACTIVITY ══════════════════════════════════════════════════════ */}
        {tab==="Activity"&&(
          <>
            {/* Type toggle */}
            <div className="flex items-center gap-2 mb-5 flex-wrap">
              {["Student","Admin","all"].map(t=>(
                <Pill key={t} label={t==="all"?"All":t+"s"} active={sessionType===t}
                  onClick={()=>{ setSessionType(t); setSessionPage(1); }}/>
              ))}
              {activityLoading&&(
                <span className="text-xs text-slate-400 animate-pulse ml-2">Loading…</span>
              )}
            </div>

            {/* Trend charts */}
            <SectionLabel text="Trends"/>
            <div className="space-y-4 mb-6">
              <Chart series={signupsSeries}  color="#3b82f6" label="Daily Signups"             total={wSignups}/>
              <Chart series={loginsSeries}   color="#8b5cf6" label="Daily Logins"              total={wLogins}/>
              <Chart series={practiceSeries} color="#10b981" label="Questions Answered per Day" total={wQTouched}/>
            </div>

            {/* Recent sessions feed — paginated */}
            {(()=>{
              const allSessions = activityData?.recent_sessions||[];
              const totalSessions = allSessions.length;
              const totalSessionPgs = Math.max(1,Math.ceil(totalSessions/SESSION_PAGE_SIZE));
              const sessionPageRows = allSessions.slice((sessionPage-1)*SESSION_PAGE_SIZE, sessionPage*SESSION_PAGE_SIZE);
              return (
                <div>
                  <SectionLabel text={`Recent sessions · ${totalSessions} total`}/>
                  <Pagination
                    page={sessionPage}
                    pageSize={SESSION_PAGE_SIZE}
                    total={totalSessions}
                    onPrev={()=>setSessionPage(p=>Math.max(1,p-1))}
                    onNext={()=>setSessionPage(p=>Math.min(totalSessionPgs,p+1))}
                    onPageSize={()=>{}}
                    hidePageSize
                  />
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-auto">
                      <table className="w-full text-left text-sm">
                        <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-400">
                          <tr>
                            {["When","Type","Name / Username","Email / Role","School","Device","IP"].map(h=>(
                              <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {totalSessions===0?(
                            <tr><td className="px-4 py-8 text-slate-400 text-sm" colSpan={7}>No sessions in this window.</td></tr>
                          ):sessionPageRows.map((s,i)=>{
                            const isStudent=s.type==="Student";
                            const name=isStudent
                              ?`${s.user?.first_name||""} ${s.user?.last_name||""}`.trim()||"—"
                              :s.user?.username||"—";
                            const emailOrRole=isStudent?s.user?.email||"—":s.user?.role||"—";
                            return (
                              <tr key={s.id||i} className="hover:bg-slate-50/60">
                                <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{fmtTime(s.created_at)}</td>
                                <td className="px-4 py-3">
                                  <span className={cl(
                                    "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
                                    isStudent
                                      ?"bg-sky-50 text-sky-700 ring-sky-200"
                                      :"bg-violet-50 text-violet-700 ring-violet-200"
                                  )}>{s.type}</span>
                                </td>
                                <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">{name}</td>
                                <td className="px-4 py-3 text-slate-500 text-xs">{emailOrRole}</td>
                                <td className="px-4 py-3 text-slate-500 text-xs">
                                  {isStudent?(
                                    <>
                                      <span>{schoolName(s.user?.school)}</span>
                                      {schoolValue(s.user?.school)&&<span style={{display:"block",fontSize:"10px",fontStyle:"italic",color:"#94a3b8"}}>{schoolValue(s.user?.school)}</span>}
                                    </>
                                  ):"—"}
                                </td>
                                <td className="px-4 py-3 text-slate-400 text-xs">{s.device_type||"—"}</td>
                                <td className="px-4 py-3 text-slate-400 text-xs font-mono">{s.ip_address||"—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        )}

        {/* ══ STUDENTS ══════════════════════════════════════════════════════ */}
        {tab==="Students"&&(
          <>
            <SectionLabel text={`Students in window · ${studentsInWindow.length} total`}/>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <select value={studentSort} onChange={e=>setStudentSort(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                <option value="newest">↓ Newest first</option>
                <option value="oldest">↑ Oldest first</option>
                <option value="name_az">Name A → Z</option>
                <option value="school_az">School A → Z</option>
                <option value="most_questions">↓ Most questions answered</option>
                <option value="least_questions">↑ Least questions answered</option>
              </select>
              <input value={studentSearch} onChange={e=>setStudentSearch(e.target.value)}
                placeholder="Search name / email / school / ID…"
                className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-700 shadow-sm flex-1 min-w-[280px]"/>
            </div>
            <Pagination page={studentPage} pageSize={studentPageSize} total={studentTotal}
              onPrev={()=>setStudentPage(p=>Math.max(1,p-1))}
              onNext={()=>setStudentPage(p=>Math.min(studentTotalPgs,p+1))}
              onPageSize={n=>{setStudentPageSize(n);setStudentPage(1);}}/>
            {/* Clickable student table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="max-h-[560px] overflow-auto">
                <table className="w-full text-left text-sm">
                  <thead className="sticky top-0 bg-slate-50 text-xs font-bold uppercase tracking-wider text-slate-400">
                    <tr>
                      {["#","Name","Email","School","Questions","Joined","ID"].map(h=>(
                        <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {studentPageRows.length===0?(
                      <tr><td className="px-4 py-8 text-slate-400 text-sm" colSpan={7}>No students in this window.</td></tr>
                    ):studentPageRows.map((r)=>(
                      <tr key={r.id||r._rank}
                        onClick={()=>setSelectedStudent(r)}
                        className="hover:bg-slate-50 cursor-pointer transition-colors"
                        title="Click to view student detail">
                        <td className="px-4 py-3 text-slate-400 text-xs font-semibold">{r._rank}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">
                          <span className="flex items-center gap-2">
                            {r.name}
                            <span className="text-slate-300 text-xs">↗</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-600">{r.email}</td>
                        <td className="px-4 py-3 text-slate-600">
                          <span>{r.school}</span>
                          {r.schoolRaw && <span style={{display:"block",fontSize:"10px",fontStyle:"italic",color:"#94a3b8",marginTop:"1px"}}>{r.schoolRaw}</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-600 text-sm font-mono">
                          {r.questionsAnswered!=null ? fmt(r.questionsAnswered) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs">{fmtTime(r.created_at)}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs font-mono">{r.id}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ══ SCHOOLS ═══════════════════════════════════════════════════════ */}
        {tab==="Schools"&&(
          <>
            <SectionLabel text="Schools ranked by student count"/>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Pill label="All time" active={schoolsMode==="all_time"} onClick={()=>setSchoolsMode("all_time")}/>
              <Pill label="Window"   active={schoolsMode==="window"}   onClick={()=>setSchoolsMode("window")}/>
              <input value={schoolSearch} onChange={e=>setSchoolSearch(e.target.value)}
                placeholder="Search school…"
                className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-700 shadow-sm flex-1 min-w-[240px]"/>
            </div>
            <Pagination page={schoolPage} pageSize={schoolPageSize} total={schoolTotal}
              onPrev={()=>setSchoolPage(p=>Math.max(1,p-1))}
              onNext={()=>setSchoolPage(p=>Math.min(schoolTotalPgs,p+1))}
              onPageSize={n=>{setSchoolPageSize(n);setSchoolPage(1);}}/>
            <DataTable
              cols={[
                {key:"_rank",        label:"Rank"},
                {key:"school", label:"School", render:r=>(
                  <span>
                    {r.school}
                    {r.schoolRaw&&<span style={{display:"block",fontSize:"10px",fontStyle:"italic",color:"#94a3b8",marginTop:"1px"}}>{r.schoolRaw}</span>}
                  </span>
                )},
                {key:"students",     label:"Students"},
                {key:"pct_of_total", label:"% of total",
                  render:r=>r.pct_of_total!=null?`${Number(r.pct_of_total).toFixed(1)}%`:"—"},
              ]}
              rows={schoolPageRows}
              empty="No schools found."/>
          </>
        )}

        {/* ══ REPORTS ═══════════════════════════════════════════════════════ */}
        {tab==="Reports"&&(
          <>
            <SectionLabel text={`Question reports · window · ${reportsInWindow.length} total`}/>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <select value={reportStatus} onChange={e=>setReportStatus(e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="all">All statuses</option>
              </select>
              <input value={reportSearch} onChange={e=>setReportSearch(e.target.value)}
                placeholder="Search question / type / description…"
                className="rounded-xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-700 shadow-sm flex-1 min-w-[280px]"/>
            </div>
            <Pagination page={reportPage} pageSize={reportPageSize} total={reportTotal}
              onPrev={()=>setReportPage(p=>Math.max(1,p-1))}
              onNext={()=>setReportPage(p=>Math.min(reportTotalPgs,p+1))}
              onPageSize={n=>{setReportPageSize(n);setReportPage(1);}}/>
            <DataTable
              cols={[
                {key:"created_at",  label:"When",       render:r=>fmtTime(r.created_at)},
                {key:"question_id", label:"Question"},
                {key:"issue_type",  label:"Type"},
                {key:"status",      label:"Status",
                  render:r=>{
                    const s=r.status;
                    const c=s==="resolved"?"bg-emerald-50 text-emerald-700 ring-emerald-200"
                             :s==="in_progress"?"bg-amber-50 text-amber-700 ring-amber-200"
                             :"bg-rose-50 text-rose-700 ring-rose-200";
                    return <span className={cl("inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ring-1",c)}>{s}</span>;
                  }
                },
                {key:"student_id",  label:"Student ID"},
                {key:"description", label:"Description",
                  render:r=>r.description?(r.description.length>80?r.description.slice(0,80)+"…":r.description):"—"},
              ]}
              rows={reportPageRows}
              empty="No reports match your filter."/>
          </>
        )}
      </main>
      <StudentDrawer student={selectedStudent} onClose={()=>setSelectedStudent(null)}/>
    </div>
  );
}